// IMPORTS

// external
import {
  Algod,
  Algodv2,
  assignGroupID,
  encodeUint64,
  getApplicationAddress,
  makeApplicationNoOpTxnFromObject,
  makeAssetTransferTxnWithSuggestedParamsFromObject,
  makePaymentTxnWithSuggestedParamsFromObject,
  Transaction
} from "algosdk"

// global
import { getApplicationGlobalState } from "../../stateUtils"
import { getParams, getPaymentTxn } from "../../transactionUtils"
import AlgofiUser from "../../algofiUser"
import { StakingQuote } from "../../staking/v2/staking"

// local
import GovernanceClient from "./governanceClient"
import { VOTING_ESCROW_STRINGS } from "./governanceConfig"

export default class VotingEscrow {
  public governanceClient: GovernanceClient
  public algod: Algodv2
  public appId: number
  public totalLocked: number
  public totalVebank: number
  public assetId: number
  public votingEscrowMaxTimeLockSeconds: number
  public votingEscrowMinTimeLockSeconds: number

  /**
   * The constructor for the voting escrow object.
   * 
   * @param governanceClient - a governance client
   */
  constructor(governanceClient: GovernanceClient) {
    this.governanceClient = governanceClient
    this.algod = this.governanceClient.algod
    this.appId = governanceClient.governanceConfig.votingEscrowAppId
    this.votingEscrowMaxTimeLockSeconds = governanceClient.governanceConfig.votingEscrowMaxTimeLockSeconds
    this.votingEscrowMinTimeLockSeconds = governanceClient.governanceConfig.votingEscrowMinTimeLockSeconds
  }

  /**
   * Function which will update the data on the voting escrow object to match
   * that of the global state of the voting escrow contract.
   */
  async loadState() {
    const globalState = await getApplicationGlobalState(this.algod, this.appId)

    this.totalLocked = globalState[VOTING_ESCROW_STRINGS.total_locked] || 0
    this.totalVebank = globalState[VOTING_ESCROW_STRINGS.total_vebank] || 0
    this.assetId = globalState[VOTING_ESCROW_STRINGS.asset_id] || 0
  }

  getLockQuote(user: AlgofiUser, lockAmount: number, lockEnd: number): [number, {[key: number] : StakingQuote}] {
    let userVotingEscrowState = user.governance.v1.userVotingEscrowState
    let userStakingState= user.staking.v2
    let newAmountLocked = (userVotingEscrowState?.amountLocked || 0) + lockAmount
    let newDurationRemaining = lockEnd - (Date.now()/1000)

    let [projAmountVeBank, projBoostMultiplier] = userVotingEscrowState.getProjValues(newAmountLocked, newDurationRemaining)
    
    let stakingQuotes = {}
    for (const [key, value] of Object.entries(this.governanceClient.algofiClient.staking.v2.stakingContracts)) {
      stakingQuotes[key] = value.getLockingAPRQuote(user, projBoostMultiplier)
    }
    
    return [projAmountVeBank, stakingQuotes]
  }

  async getAirdropTxns(user: AlgofiUser): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()
    const txns = []
    
    params.fee = 3000
    txns.push(getPaymentTxn(params, user.address, user.address, this.assetId, 0))
    
    params.fee = 0
    txns.push(
      makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: user.governance.v1.userAirdropState.dropLsig.address(),
        to: user.address,
        assetIndex: this.assetId,
        amount: user.governance.v1.userAirdropState.unlockedAirdrop,
        suggestedParams: params,
        closeRemainderTo: user.address,
        rekeyTo: undefined,
        revocationTarget: undefined
      })
    )
    
    txns.push(
      makePaymentTxnWithSuggestedParamsFromObject({
        from: user.governance.v1.userAirdropState.dropLsig.address(),
        to: "RF5KSQEGCGO5WPRU6B7VZHLY7AIOGY2K4ERCVHGWAJ3GG3X5VA5J5V3PRA",
        amount: 200000,
        suggestedParams: params,
        closeRemainderTo: "RF5KSQEGCGO5WPRU6B7VZHLY7AIOGY2K4ERCVHGWAJ3GG3X5VA5J5V3PRA",
        rekeyTo: undefined,
      })
    )

    return assignGroupID(txns)
  }

  async getLockedAirdropTxns(user: AlgofiUser): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()
    const txns = []
    
    params.fee = 0
    txns.push(
      makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: user.governance.v1.userAirdropState.lockLsig.address(),
        to: getApplicationAddress(this.appId),
        assetIndex: this.assetId,
        amount: user.governance.v1.userAirdropState.lockedAirdrop,
        suggestedParams: params,
        closeRemainderTo: getApplicationAddress(this.appId),
        rekeyTo: undefined,
        revocationTarget: undefined
      })
    )
    
    params.fee = 3000
    txns.push(
      makeApplicationNoOpTxnFromObject({
        from: user.address,
        appIndex: this.appId,
        appArgs: [enc.encode(VOTING_ESCROW_STRINGS.lock), encodeUint64(60*60*24*180)], // 180 days
        suggestedParams: params,
        accounts: undefined,
        foreignAssets: undefined,
        foreignApps: undefined,
        rekeyTo: undefined
      })
    )
    
    params.fee = 0
    txns.push(
      makePaymentTxnWithSuggestedParamsFromObject({
        from: user.governance.v1.userAirdropState.lockLsig.address(),
        to: "RF5KSQEGCGO5WPRU6B7VZHLY7AIOGY2K4ERCVHGWAJ3GG3X5VA5J5V3PRA",
        amount: 200000,
        suggestedParams: params,
        closeRemainderTo: "RF5KSQEGCGO5WPRU6B7VZHLY7AIOGY2K4ERCVHGWAJ3GG3X5VA5J5V3PRA",
        rekeyTo: undefined,
      })
    )
    
    let update_txns = await this.governanceClient.algofiClient.staking.v2.getUpdateUserTxns(user);
    for (const txn of update_txns) {
      txns.push(txn)
    }

    return assignGroupID(txns)
  }


  /**
   * Constructs a series of transactions to update a target user's vebank.
   * 
   * @param userCalling - user who is calling the udpate transaction
   * @param userUpdating - user whose vebank is actually being updated
   * @returns a series of transactions to update a target user's vebank.
   */
  async getUpdateVeBankDataTxns(userCalling: AlgofiUser, userUpdating: AlgofiUser): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    const updateUserVebankDataTxn = makeApplicationNoOpTxnFromObject({
      from: userCalling.address,
      appIndex: this.appId,
      appArgs: [enc.encode(VOTING_ESCROW_STRINGS.update_vebank_data)],
      suggestedParams: params,
      accounts: [userUpdating.address],
      foreignAssets: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })

    return [updateUserVebankDataTxn]
  }

  /**
   * Constructs a series of transactions that lock a user's BANK.
   * 
   * @param user - user who is locking
   * @param amount - amount they are locking
   * @param durationSeconds - amount of time they are locking for 
   * @returns a series of transactions that lock a user's BANK.
   */
  async getLockTxns(user: AlgofiUser, amount: number, durationSeconds: number): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()
    const txns = []
    
    if (user.governance.v1.userAirdropState.lockedAirdrop > 0) {
      throw 'User has airdrop to claim!';
    }

    const govTokenTxn = makeAssetTransferTxnWithSuggestedParamsFromObject({
      from: user.address,
      to: getApplicationAddress(this.appId),
      assetIndex: this.governanceClient.governanceConfig.governanceToken,
      amount: amount,
      suggestedParams: params,
      rekeyTo: undefined,
      revocationTarget: undefined
    })

    const lockTxn = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(VOTING_ESCROW_STRINGS.lock), encodeUint64(durationSeconds)],
      suggestedParams: params,
      accounts: undefined,
      foreignAssets: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })

    txns.push(govTokenTxn, lockTxn)
    
    let update_txns = await this.governanceClient.algofiClient.staking.v2.getUpdateUserTxns(user);
    for (const txn of update_txns) {
      txns.push(txn)
    }
    
    return assignGroupID(txns)
  }

  /**
   * Constructs a series of transactions that extend a user's lock.
   * 
   * @param user - user who is locking
   * @param durationSeconds - amount of time they are extending for
   * @returns a series of transactions that extend a user's lock.
   */
  async getExtendLockTxns(user: AlgofiUser, durationSeconds: number): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()
    const txns = []

    const extendLockTxn = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(VOTING_ESCROW_STRINGS.extend_lock), encodeUint64(durationSeconds)],
      suggestedParams: params,
      accounts: undefined,
      foreignAssets: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })
    txns.push(extendLockTxn)

    let update_txns = await this.governanceClient.algofiClient.staking.v2.getUpdateUserTxns(user);
    for (const txn of update_txns) {
      txns.push(txn)
    }

    return assignGroupID(txns)
  }

  /**
   * Constructs a series of transactions that increase a user's lock amount.
   * 
   * @param user - user who is locking
   * @param amount - amount they are increasing their lock for
   * @returns a series of transactions that increase a user's lock amount.
   */
  async getIncreaseLockAmountTxns(user: AlgofiUser, amount: number): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()
    const txns = []

    const govTokenTxn = makeAssetTransferTxnWithSuggestedParamsFromObject({
      from: user.address,
      to: getApplicationAddress(this.appId),
      assetIndex: this.governanceClient.governanceConfig.governanceToken,
      amount: amount,
      suggestedParams: params,
      rekeyTo: undefined,
      revocationTarget: undefined
    })

    const increaseLockAmountTxns = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(VOTING_ESCROW_STRINGS.increase_lock_amount)],
      suggestedParams: params,
      accounts: undefined,
      foreignAssets: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })

    txns.push(govTokenTxn, increaseLockAmountTxns)

    let update_txns = await this.governanceClient.algofiClient.staking.v2.getUpdateUserTxns(user);
    for (const txn of update_txns) {
      txns.push(txn)
    }

    return assignGroupID(txns)
  }

  async getClaimTxns(user: AlgofiUser): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()
    params.fee = 2000

    const claimTxn = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(VOTING_ESCROW_STRINGS.claim)],
      suggestedParams: params,
      foreignAssets: [this.governanceClient.governanceConfig.governanceToken],
      accounts: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })

    return [claimTxn]
  }
}
